import { defineStore } from 'pinia';
import {
  getAccentDefinition,
  getVuetifyThemeName,
  normalizeAccentFamily,
  normalizeThemeMode,
} from '../theme/accentThemes';

const APPEARANCE_UPDATED_AT_KEY = 'bm_appearance_updated_at';
const APPEARANCE_USER_ID_KEY = 'bm_appearance_user_id';

function getUserId(user) {
  return user?._id || user?.id || '';
}

export const useUiStore = defineStore('ui', {
  state: () => ({
    theme: normalizeThemeMode(localStorage.getItem('bm_theme') || 'dark'),
    themeAccent: normalizeAccentFamily(localStorage.getItem('bm_theme_accent') || 'standard'),
    appearanceUpdatedAt: Number(localStorage.getItem(APPEARANCE_UPDATED_AT_KEY) || 0),
    sideNavOpen: localStorage.getItem('bm_side_nav_open') !== 'false',
    confirmDialog: null, // { title, message, onConfirm }
  }),
  getters: {
    vuetifyThemeName: (state) => getVuetifyThemeName(state.theme, state.themeAccent),
    themeClass: (state) => (state.theme === 'dark' ? 'theme--dark' : 'theme--light'),
    accentClass: (state) => `bm-accent-${state.themeAccent}`,
    accentDefinition: (state) => getAccentDefinition(state.themeAccent),
    accentCssVars: (state) => {
      const accent = getAccentDefinition(state.themeAccent);
      return {
        '--bm-primary': accent.primary,
        '--bm-primary-dark': accent.primaryDark,
        '--bm-secondary': accent.secondary,
        '--bm-secondary-dark': accent.secondaryDark,
        '--bm-accent': accent.accent,
        '--bm-accent-rgb': accent.accentRgb,
      };
    },
  },
  actions: {
    touchAppearance(userId) {
      this.appearanceUpdatedAt = Date.now();
      localStorage.setItem(APPEARANCE_UPDATED_AT_KEY, String(this.appearanceUpdatedAt));
      if (userId) localStorage.setItem(APPEARANCE_USER_ID_KEY, userId);
    },
    markAppearanceOwner(userId) {
      if (userId) localStorage.setItem(APPEARANCE_USER_ID_KEY, userId);
    },
    getLocalAppearance() {
      return {
        theme: normalizeThemeMode(localStorage.getItem('bm_theme') || this.theme),
        themeAccent: normalizeAccentFamily(localStorage.getItem('bm_theme_accent') || this.themeAccent),
      };
    },
    isLocalAppearanceNewerThan(user) {
      const userId = getUserId(user);
      const ownerId = localStorage.getItem(APPEARANCE_USER_ID_KEY);
      // Only defer to the server when we know a *different* account owns the
      // local appearance value (shared/public device). A missing ownerId just
      // means this device hasn't recorded an owner yet — it should not be
      // treated as "no local preference exists" and silently overwritten.
      if (ownerId && userId && ownerId !== userId) return false;
      const localUpdatedAt = Number(localStorage.getItem(APPEARANCE_UPDATED_AT_KEY) || this.appearanceUpdatedAt || 0);
      if (!localUpdatedAt) return false;
      const serverUpdatedAt = Date.parse(user?.updatedAt || '') || 0;
      return localUpdatedAt > serverUpdatedAt + 500;
    },
    setTheme(theme, options = {}) {
      this.theme = normalizeThemeMode(theme);
      localStorage.setItem('bm_theme', this.theme);
      if (options.touch !== false) this.touchAppearance(options.userId);
    },
    setThemeAccent(accent, options = {}) {
      this.themeAccent = normalizeAccentFamily(accent);
      localStorage.setItem('bm_theme_accent', this.themeAccent);
      if (options.touch !== false) this.touchAppearance(options.userId);
    },
    setAppearance({ theme, themeAccent }, options = {}) {
      const nextTheme = theme ? normalizeThemeMode(theme) : this.theme;
      const nextAccent = themeAccent ? normalizeAccentFamily(themeAccent) : this.themeAccent;
      this.theme = nextTheme;
      this.themeAccent = nextAccent;
      localStorage.setItem('bm_theme', this.theme);
      localStorage.setItem('bm_theme_accent', this.themeAccent);
      if (options.userId) this.markAppearanceOwner(options.userId);
      if (options.touch !== false) this.touchAppearance(options.userId);
    },
    toggleTheme() {
      this.setTheme(this.theme === 'light' ? 'dark' : 'light');
    },
    toggleSideNav() {
      this.sideNavOpen = !this.sideNavOpen;
      localStorage.setItem('bm_side_nav_open', this.sideNavOpen ? 'true' : 'false');
    },
    /** All deletions in the system route through this — see requirement #8. */
    confirm({ title, message, confirmText = 'Delete', confirmColor = 'error', onConfirm }) {
      this.confirmDialog = { title, message, confirmText, confirmColor, onConfirm };
    },
    closeConfirm() {
      this.confirmDialog = null;
    },
  },
});
